/**
 * DeckBrowser — full-screen deck management surface. Shows the same
 * "Your decks" / "Starter decks" split the Lobby uses, but as a 2/3-col
 * responsive grid of richer tiles with explicit View / Clone & edit
 * actions.
 *
 * Reachable via `?deck=browse`. Distinct from the Lobby's compact
 * match-start picker, which stays in place for quick deck selection
 * before a match. This surface is the place to study what's available,
 * clone a starter, or trash one of your own builds.
 */

import { type ReactElement, useMemo, useState } from 'react';
import { useCards } from '../hooks/useCards.js';
import { useDecks } from '../hooks/useDecks.js';
import type { DeckData } from '../types.js';
import { dominantOrbital, orbitalColor } from '../types.js';
import { DeckView } from './DeckView.js';

type Props = {
  onCreateNew: () => void;
  onCloneDeck: (sourceDeckId: string) => void;
  onBack: () => void;
};

export function DeckBrowser({ onCreateNew, onCloneDeck, onBack }: Props): ReactElement {
  const decks = useDecks();
  const cards = useCards();
  const [viewing, setViewing] = useState<DeckData | null>(null);
  const [busyCloneId, setBusyCloneId] = useState<string | null>(null);

  const userDecks = useMemo(() => decks.decks.filter((d) => !d.is_tournament), [decks.decks]);
  const starterDecks = useMemo(() => decks.decks.filter((d) => d.is_tournament), [decks.decks]);

  const handleClone = async (deckId: string) => {
    setBusyCloneId(deckId);
    try {
      onCloneDeck(deckId);
    } finally {
      setBusyCloneId(null);
    }
  };

  const handleDelete = async (deckId: string) => {
    if (!confirm("Delete this deck? This can't be undone.")) return;
    await decks.remove(deckId);
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-8">
      <header className="flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-cosmos-400 hover:text-cosmos-200"
          >
            ← Back to lobby
          </button>
          <h1 className="font-display mt-1 text-3xl text-amber-400">Decks</h1>
          <p className="mt-1 text-sm text-cosmos-400">
            Browse, clone, and manage your decks. Click a tile to view its cards.
          </p>
        </div>
        <button
          type="button"
          onClick={onCreateNew}
          className="rounded bg-violet-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-500"
        >
          Start a fresh build
        </button>
      </header>

      {decks.status === 'loading' ? (
        <p className="text-xs italic text-cosmos-500">Loading decks…</p>
      ) : decks.status === 'error' ? (
        <p className="text-xs text-rose-400">Failed to load decks: {decks.error}</p>
      ) : (
        <>
          <Section title="Your decks">
            {userDecks.length === 0 ? (
              <EmptyState onCreateNew={onCreateNew} />
            ) : (
              <Grid>
                {userDecks.map((d) => (
                  <DeckTile
                    key={d.deck_id}
                    deck={d}
                    catalog={cards.cards}
                    busy={busyCloneId === d.deck_id}
                    onView={() => setViewing(d)}
                    onClone={() => handleClone(d.deck_id)}
                    onDelete={() => void handleDelete(d.deck_id)}
                  />
                ))}
              </Grid>
            )}
          </Section>

          {starterDecks.length > 0 ? (
            <Section title="Starter decks">
              <Grid>
                {starterDecks.map((d) => (
                  <DeckTile
                    key={d.deck_id}
                    deck={d}
                    catalog={cards.cards}
                    busy={busyCloneId === d.deck_id}
                    onView={() => setViewing(d)}
                    onClone={() => handleClone(d.deck_id)}
                  />
                ))}
              </Grid>
            </Section>
          ) : null}
        </>
      )}

      {viewing ? (
        <DeckView
          deck={viewing}
          onClose={() => setViewing(null)}
          onClone={() => {
            const id = viewing.deck_id;
            setViewing(null);
            void handleClone(id);
          }}
        />
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactElement }): ReactElement {
  return (
    <section>
      <h2 className="mb-2 text-[11px] uppercase tracking-wide text-cosmos-500">{title}</h2>
      {children}
    </section>
  );
}

function Grid({ children }: { children: ReactElement | ReactElement[] }): ReactElement {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">{children}</div>;
}

function EmptyState({ onCreateNew }: { onCreateNew: () => void }): ReactElement {
  return (
    <div className="rounded-md border border-dashed border-cosmos-700 bg-cosmos-900/40 px-4 py-6 text-center">
      <p className="text-sm text-cosmos-300">
        No decks yet — start a fresh build, or browse a starter to clone.
      </p>
      <button
        type="button"
        onClick={onCreateNew}
        className="mt-3 rounded bg-violet-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-500"
      >
        Start a fresh build
      </button>
    </div>
  );
}

type DeckTileProps = {
  deck: DeckData;
  catalog: ReadonlyMap<string, { orbital?: string | null }>;
  busy: boolean;
  onView: () => void;
  onClone: () => void;
  onDelete?: () => void;
};

function DeckTile({ deck, catalog, busy, onView, onClone, onDelete }: DeckTileProps): ReactElement {
  const orbital = useMemo(() => dominantOrbital(deck.card_ids, catalog), [deck.card_ids, catalog]);
  return (
    <article className="group relative flex overflow-hidden rounded-md border border-cosmos-800 bg-cosmos-900/50 transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className={`w-1 ${orbitalColor(orbital)}`} aria-hidden />
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-cosmos-50">{deck.name}</h3>
          {deck.is_tournament ? (
            <span className="rounded bg-yellow-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-yellow-300">
              Starter
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-cosmos-400">
          <span className="font-mono">{deck.card_ids.length} cards</span>
          {orbital ? <span>{orbital}</span> : null}
        </div>
        <div className="mt-auto flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onView}
            className="rounded bg-cosmos-700 px-2.5 py-1 text-xs hover:bg-cosmos-600"
          >
            View
          </button>
          <button
            type="button"
            onClick={onClone}
            disabled={busy}
            className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Cloning…' : 'Clone & edit'}
          </button>
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="ml-auto text-[11px] text-cosmos-500 hover:text-rose-400"
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
